/* eslint-disable no-param-reassign */
import {
  put,
  takeLatest,
  takeEvery,
  select,
  delay,
  all,
  call
} from 'redux-saga/effects';
import storage from '@xto10x/storage/lib';
import objectivesApi from 'services/objectivesApi';
import teamsApi from 'services/teamsApi';
import sheetsIntegrationApi from 'services/sheetsIntegrationApi';
import {
  actions as appActions,
  showAppLoading,
  hideAppLoading,
  showAlertMessage,
  fetchTeamsData,
  saveHighlightedObjective,
  fetchObjectivesForSelectedParams,
  saveCompanyTagsList,
  resetSearchParamsAction,
  handleApiCallError
} from 'containers/App/store/actions';

import {
  getValueFromQueryParam,
  clone,
  convertDateToString
} from '@xto10x/core/lib/app.utils';
import {
  isHeaderLeftTabs,
  getChannel,
  appendOrUpdateQueryParam,
  removeQueryParam,
  isStrategicCycle,
  appendCustomRangeQueryParamsToRequest
} from 'utils/app.utils';
import exportObjectivesUtil from 'utils/exportObjectivesUtil';
import {
  HeaderLeftTabs,
  QueryParam,
  ErrorMsg,
  ToastActionType,
  AlertActionTypes,
  StorageKey,
  TeamActions,
  ManagerApprovalStatus,
  ObjectiveTypes,
  DefaultObjectivesCount
} from 'utils/constants';
import analytics from '@xto10x/core/lib/analytics';
import { AnalyticsCategory } from 'utils/analytics.constants';
import {
  getSelectedTeamSelector,
  getSearchObjectiveParams
} from 'containers/Header/store/reducer';
import { updateSearchQueryString } from 'containers/Header/store/actions';
import {
  getCurrentUserInfoSelector,
  getDeviceDetailsSelector
} from 'containers/App/store/reducer';
import {
  fetchObjectiveDetails,
  fetchObjectiveActivities,
  saveJiraIntegrationLatestProgress,
  viewInContextRequest
} from 'containers/ObjectiveActivity/store/actions';
import { getCurrentVersionOfObjective } from 'utils/objectives.utils';
import { pushDependencyObjectivesAtTheEnd } from 'utils/objectiveUtils.mobile';
import sortTagsByUsage, {
  updateTagUsageCount,
  updateCompanyTagsList
} from 'utils/tag.utils';
import { reorderObjectivesUnderDiffParent } from 'containers/Home/store/actions';
import { push } from 'connected-react-router';
import {
  actions,
  saveObjectives,
  saveNewKR,
  saveNewDependencyObjective,
  saveObjectiveAlignment,
  setSelectedObjective,
  updateObjective,
  updateObjectiveToggleKrs,
  updateObjectivesDataRealtimeEvent,
  setMoveObjective,
  setObjectiveToLink,
  deleteObjectiveInList,
  fetchKeyResultsRequest,
  saveLinkedObjectives,
  unLinkObjectiveRequest,
  deleteDependencyFromObjectives,
  saveViewDashboardData,
  fetchFilteredObjectives,
  storeRecentlyUpdatedObjectiveDetails,
  clearAlignedObjective,
  saveMobileTraversedTreeData,
  exitTreeView,
  showObjectiveLoader,
  updateViewDashboardData,
  fetchTeamDetails,
  saveTeamDetails,
  setQuickFilterAttributes,
  setQuickFiltersSelected,
  updateTeamQuickFilterAttributes,
  updateObjectivesVisibility,
  resetPaginationData,
  fetchDashboardFilteredObjectives,
  fetchQuickFilterObjectives,
  updatePageLoadInProgress,
  setLastObjectiveInViewId,
  updateObjectives
} from './actions';
import {
  getSelectedObjectiveToMoveSelector,
  getSelectedObjectiveToLinkSelector,
  getPathMapSelector,
  getObjectivesSelector,
  getMobileTraversedTreeData,
  getPaginationData,
  getSelectedQuickFilters,
  getSelectedTeamQuickFilters,
  getLastObjectiveInViewId,
  getViewAlignmentPreviousViewState
} from './reducer';
import { setFilters } from '../../Header/store/actions';
import { getCompanyTagsSelector } from '../../App/store/reducer';
import {
  showCompanyObjectiveSelector,
  showIndependentObjectiveSelector,
  getFilterParamsSelector,
  getSelectedObjectiveCycleSelector,
  getDashboardFilterSelector,
  getShowProtectedObjectivesSelector,
  getShowCustomObjectivesSelector
} from '../../Header/store/reducer';
import { showOKRHierarchyToast } from '../../Home/store/actions';
import { ViewDashboardConfig } from '../../../utils/constants';
import {
  getFiltersRequestBodyFromFilterParams,
  transformDemographicObjectiveSummary
} from './utils';

function* fetchObjectivesRequestWorker({
  requestBody,
  callback,
  appendObjectives,
  fetchAllObjectives
}) {
  try {
    if (!appendObjectives) yield put(showObjectiveLoader(true));
    const paginationData = yield select(getPaginationData);
    if (!fetchAllObjectives) {
      requestBody.limit = DefaultObjectivesCount;
      requestBody.page = 1;
      if (!appendObjectives && paginationData.currentPage > 1)
        yield put(resetPaginationData());
      if (appendObjectives) requestBody.page = paginationData.currentPage;
    }
    if (requestBody.date_range_start && requestBody.date_range_end)
      delete requestBody.okr_cycle_id;
    const data = yield objectivesApi
      .getObjectivesList(requestBody)
      .then(res => res.data);

    // Remove all key_results from all objectives
    const objectives = [];
    data.objective_models.forEach(item => {
      objectives.push({
        ...item,
        key_results: []
      });
    });
    if (!appendObjectives) yield put(saveObjectives(null));
    yield put(
      saveObjectives(
        objectives,
        data.ordering_version,
        {
          total_objectives: data.total_objectives,
          metadata: data.metadata
        },
        appendObjectives &&
          !fetchAllObjectives &&
          paginationData.currentPage > 1
      )
    );
    if (appendObjectives) {
      const lastObjectiveInViewId = yield select(getLastObjectiveInViewId);
      const ele = document.querySelector(
        `[data-id="${lastObjectiveInViewId}"]`
      );
      yield put(delay(100));
      if (lastObjectiveInViewId && ele)
        ele.scrollIntoView({ behavior: 'smooth' });
      yield put(setLastObjectiveInViewId(null));
    }
    if (callback) {
      callback(objectives);
    }
    yield put(setQuickFiltersSelected(null));
    if (!isHeaderLeftTabs(requestBody?.tag_id || requestBody?.team_id)) {
      const request = {
        root_team_id: requestBody?.tag_id || requestBody?.team_id,
        okr_cycle_id: requestBody.okr_cycle_id
      };
      appendCustomRangeQueryParamsToRequest(request);
      const info = yield objectivesApi
        .getFilterAttributes(request)
        .then(res => res.data);
      yield put(setQuickFilterAttributes(info));
    } else {
      yield put(setQuickFilterAttributes({}));
    }
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
  } catch (e) {
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_objectives_request_worker', e);
  }
}

function* updateOwnerRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjectiveOwner(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    yield put(updateTeamQuickFilterAttributes());
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Owner changed'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_owner_request_worker', e);
  }
}

function* updateProgressRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective,
  callback
}) {
  try {
    const deviceDetails = yield select(getDeviceDetailsSelector);
    yield put(showAppLoading());
    if (requestBody.check_in_as_on_date === convertDateToString(new Date()))
      delete requestBody.check_in_as_on_date;
    const { data: checkInData } = yield objectivesApi.updateObjectiveProgress(
      objective.objective_id,
      {
        channel: getChannel(deviceDetails),
        ...requestBody
      }
    );
    const { data: objectiveData } = yield objectivesApi.getObjectiveDetail(
      objective.objective_id
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    try {
      if (
        checkInData?.message &&
        requestBody?.message &&
        checkInData.message !== requestBody.message
      ) {
        analytics.trackError(
          'make_check_in_posted_data_mismatch',
          new Error(
            `Objective id: ${objective.objective_id}, check-in id: ${checkInData.comment_id}`
          )
        );
      }
    } catch (err) {
      analytics.trackError('check_in_data_mismatch_instrumentation_error', err);
    }
    if (updateSelectedObjective) {
      yield put(
        setSelectedObjective(objectiveData, requestBody.parent_objective_owner)
      );
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...objectiveData,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(
      saveJiraIntegrationLatestProgress({
        objective_id: objective.objective_id,
        reset: 'reset'
      })
    );
    if (callback) callback();
    yield put(showAlertMessage('success', 'Progress/Status updated'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(
      handleApiCallError(e, {
        type: 'CHECK_IN',
        data: { requestBody, objective, updateSelectedObjective, callback }
      })
    );
    analytics.trackError('update_progress_request_worker', e);
  }
}

function* updateTeamsRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { tags_added, tags_removed } = requestBody;
    delete requestBody.tags_added;
    delete requestBody.tags_removed;
    const { data } = yield objectivesApi.updateObjectiveTeams(
      objective.objective_id,
      requestBody
    );
    yield put(updateTeamQuickFilterAttributes());
    if (requestBody.tag) {
      /* Updates the global tags usage count by user */
      const companyTags = yield select(getCompanyTagsSelector);
      let updatedCompanyTagsList = updateCompanyTagsList(
        companyTags,
        tags_removed,
        tags_added
      );
      updatedCompanyTagsList = sortTagsByUsage(updatedCompanyTagsList);
      yield put(saveCompanyTagsList(updatedCompanyTagsList));
      data.objective_tags = updateTagUsageCount(
        data.objective_tags,
        updatedCompanyTagsList
      );
      data.objective_tags = sortTagsByUsage(data.objective_tags);
    }

    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    if (!requestBody.tag)
      yield put(showAlertMessage('success', 'Objective team updated'));
    else if (tags_added.length === 0 && tags_removed.length > 0) {
      yield put(
        showAlertMessage(
          AlertActionTypes.NO_ICON,
          '🏷️ Your objective has been un-tagged'
        )
      );
    } else
      yield put(
        showAlertMessage(
          AlertActionTypes.NO_ICON,
          '🏷️ Your objective has been tagged'
        )
      );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_teams_request_worker', e);
  }
}

function* updateDueDateRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateDueDate(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Objective due date updated'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_due_date_request_worker', e);
  }
}

function* updateStartDateRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateStartDate(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Objective start date updated'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_start_date_request_worker', e);
  }
}

function* updateStatusRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjectiveStatus(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(
      saveJiraIntegrationLatestProgress({
        objective_id: objective.objective_id,
        reset: 'reset'
      })
    );
    const message =
      requestBody && requestBody.objective_status === 'ACCEPTED'
        ? 'accepted'
        : 'declined';
    yield put(showAlertMessage('success', `Objective ${message}`));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_status_request_worker', e);
  }
}

function* updateManagerApprovalStatusRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateManagerApprovalStatus(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    const message =
      requestBody &&
      requestBody.manager_approval_status === ManagerApprovalStatus.APPROVED
        ? 'approved'
        : 'rejected';
    yield put(showAlertMessage('success', `Objective ${message}`));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_manager_approval_request_worker', e);
  }
}

function* updateManagerApprovalStatusForKrsRequestWorker({ objective }) {
  try {
    const filters = yield select(getFilterParamsSelector);
    const paginationData = yield select(getPaginationData);
    const selectedTeam = yield select(getSelectedTeamSelector);
    yield put(showAppLoading());
    const { data } = yield objectivesApi.approveObjectiveAndSubtree(
      objective.objective_id
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (
      selectedTeam.tag_id === HeaderLeftTabs[1].tag_id &&
      filters?.manager_approval_status &&
      filters?.manager_approval_status.length === 1 &&
      filters?.manager_approval_status.includes('PENDING') &&
      paginationData.metadata?.total > 0
    )
      yield put(fetchFilteredObjectives(filters, false, null));
    else yield put(updateObjectives(data.objective_models));
    yield put(
      showAlertMessage(
        'success',
        `${data.objective_models?.length} objectives were successfully approved`
      )
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_manager_approval_for_krs_worker', e);
  }
}

function* resendForManagerApprovalRequestWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.requestManagerApproval(
      objective.objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Request sent for re-approval'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('resend_for_manager_approval_request_worker', e);
  }
}

function* updateTeamFollowerWorker({ team_id, requestBody, individualAction }) {
  try {
    yield put(showAppLoading());
    yield teamsApi.updateTeamUsers(team_id, requestBody);
    yield put(fetchTeamsData('USER'));
    if (!individualAction) yield put(fetchTeamDetails(team_id));
    yield put(
      showAlertMessage(
        'success',
        individualAction
          ? `Team ${
              requestBody.action === TeamActions.FOLLOW.toUpperCase()
                ? 'followed'
                : 'unfollowed'
            }`
          : `${requestBody.user_ids.length === 1 ? 'Follower' : 'Followers'} ${
              requestBody.action === TeamActions.FOLLOW.toUpperCase()
                ? 'added'
                : 'removed'
            }`
      )
    );
    yield put(hideAppLoading());
  } catch (error) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
    analytics.trackError('update_team_follower_worker', error);
  }
}

function* updateTeamOwnerWorker({ team_id, requestBody }) {
  try {
    yield put(showAppLoading());
    yield teamsApi.updateTeamUsers(team_id, requestBody);
    yield put(fetchTeamDetails(team_id));
    yield put(
      showAlertMessage(
        'success',
        `${requestBody.user_ids.length === 1 ? 'Owner' : 'Owners'} ${
          requestBody.action === TeamActions.ADD_OWNERS.toUpperCase()
            ? 'added'
            : 'removed'
        }`
      )
    );
    yield put(hideAppLoading());
  } catch (error) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
    analytics.trackError('update_team_owner_worker', error);
  }
}

function* deleteObjectiveWorker({ objective, delete_subtree }) {
  try {
    yield put(showAppLoading());
    const linked_objectives = yield objectivesApi
      .getLinkedToObjectives(objective.objective_id)
      .then(res => res.data.objective_models);
    const linked_objective_ids = linked_objectives.map(
      linked_objective => linked_objective.objective_id
    );
    yield put(
      deleteDependencyFromObjectives(
        linked_objective_ids,
        objective.objective_id,
        true
      )
    );
    yield objectivesApi.deleteObjective(objective.objective_id, delete_subtree);
    yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
    const selectedObjectiveToMove = yield select(
      getSelectedObjectiveToMoveSelector
    );
    if (
      selectedObjectiveToMove &&
      selectedObjectiveToMove.objective_id === objective.objective_id
    ) {
      yield put(setMoveObjective(null));
    }
    const selectedObjectiveToLink = yield select(
      getSelectedObjectiveToLinkSelector
    );
    if (
      selectedObjectiveToLink &&
      selectedObjectiveToLink.objective_id === objective.objective_id
    ) {
      yield put(setObjectiveToLink(null));
    }
    yield put(deleteObjectiveInList(objective));
    yield put(updateViewDashboardData(true));
    if (!delete_subtree && objective.key_results_count > 0)
      yield put(fetchObjectivesForSelectedParams(true));
    yield put(hideAppLoading());
    const message = objective.depth
      ? `Key result${delete_subtree ? 's' : ''}`
      : `Objective${delete_subtree ? 's' : ''}`;
    yield put(showAlertMessage('success', `${message} deleted`));
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('delete_objective_worker', e);
  }
}

function* fetchKeyResultsRequestWorker({ objective, callback }) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.getObjectiveDetail(
      objective.objective_id
    );
    const obj = {
      ...data,
      rootObjectiveIndex: objective.rootObjectiveIndex,
      highlights: objective.highlights,
      dependency_objective: objective.dependency_objective,
      isOpen: true
    };
    yield put(updateObjectiveToggleKrs(obj));
    if (callback) {
      yield delay(200);
      callback();
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_key_results_request_worker', e);
  }
}

function* toggleObjectiveKrsMobileWorker({ objective }) {
  try {
    const mobileTreeTraverseData = yield select(getMobileTraversedTreeData);
    if (mobileTreeTraverseData?.order?.includes(objective.objective_id)) {
      const matchPosition = mobileTreeTraverseData.order.indexOf(
        objective.objective_id
      );
      yield put(showAppLoading());
      const { data: objectiveData } = yield objectivesApi.getObjectiveDetail(
        objective.objective_id
      );
      yield put(updateObjective(objectiveData, true));
      yield put(
        saveMobileTraversedTreeData({
          order: mobileTreeTraverseData.order.slice(0, matchPosition + 1),
          krs: pushDependencyObjectivesAtTheEnd(
            objectiveData.key_results.map(obj => ({ ...obj, isChild: true }))
          )
        })
      );
      yield put(hideAppLoading());
      return;
    }
    if (
      mobileTreeTraverseData?.order?.lastIndexOf(objective.objective_id) !==
      mobileTreeTraverseData?.order?.length - 1
    ) {
      yield put(showAppLoading());
      const { data } = yield objectivesApi.getObjectiveDetail(
        objective.objective_id
      );
      yield put(updateObjective(data, true));
      const updatedOrder = [
        ...(mobileTreeTraverseData?.order || []),
        objective.objective_id
      ];
      yield put(
        saveMobileTraversedTreeData({
          order: updatedOrder,
          krs: pushDependencyObjectivesAtTheEnd(
            data.key_results.map(obj => ({ ...obj, isChild: true }))
          )
        })
      );
      yield put(
        push(
          appendOrUpdateQueryParam(
            QueryParam.TREE_TRAVERSE_DEPTH,
            updatedOrder.length
          )
        )
      );
      yield put(hideAppLoading());
    }
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('toggle_objectives_kr_mobile_request_worker', e);
  }
}

function* goBackToPreviousStateMobileWorker() {
  try {
    const mobileTreeTraverseData = yield select(getMobileTraversedTreeData);
    if (!mobileTreeTraverseData) return;
    if (mobileTreeTraverseData?.order?.length > 1) {
      const updatedTraverseOrder = mobileTreeTraverseData.order.slice(
        0,
        mobileTreeTraverseData.order.length - 1
      );
      yield put(showAppLoading());
      const { data } = yield objectivesApi.getObjectiveDetail(
        updatedTraverseOrder[updatedTraverseOrder.length - 1]
      );
      yield put(updateObjective(data, true));
      yield put(
        saveMobileTraversedTreeData({
          order: updatedTraverseOrder,
          krs: pushDependencyObjectivesAtTheEnd(
            data.key_results.map(obj => ({ ...obj, isChild: true }))
          )
        })
      );
      yield put(
        push(
          appendOrUpdateQueryParam(
            QueryParam.TREE_TRAVERSE_DEPTH,
            updatedTraverseOrder.length
          )
        )
      );
      yield put(hideAppLoading());
    }
    if (mobileTreeTraverseData?.order?.length === 1) yield put(exitTreeView());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('go_back_to_previous_list_view_state_worker', e);
  }
}

function* exitTreeViewWorker() {
  try {
    yield put(saveMobileTraversedTreeData(null));
    yield put(push(removeQueryParam(QueryParam.TREE_TRAVERSE_DEPTH)));
  } catch (e) {
    analytics.trackError('exit_tree_view_worker', e);
  }
}

function* fetchSelectedObjectiveWorker({ objective_id }) {
  if (objective_id == null) return;
  try {
    yield put(showAppLoading());
    const data = yield objectivesApi
      .getObjectiveDetail(objective_id, { INCLUDE_PARENT: true })
      .then(res => res.data);
    const companyTags = yield select(getCompanyTagsSelector);
    data.objective_tags = updateTagUsageCount(data.objective_tags, companyTags);
    data.objective_tags = sortTagsByUsage(data.objective_tags);
    yield put(setSelectedObjective(data, data.parent_objective_owner));
    yield put(clearAlignedObjective());
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_selected_objective_worker', e);
  }
}

function* fetchObjectiveAlignmentWorker({ objective }) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.getObjectiveAlignment(
      objective.objective_id
    );
    data.alignment_of = objective;
    yield put(saveObjectiveAlignment(data, objective));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_objective_alignment_worker', e);
  }
}

function* fetchFilteredObjectivesWorker({
  filters,
  via_view_dashboard,
  callback,
  appendObjectives,
  disablePagination
}) {
  try {
    const selectedTeam = yield select(getSelectedTeamSelector);
    const userInfo = yield select(getCurrentUserInfoSelector);
    const searchParams = yield select(getSearchObjectiveParams);
    const params = getFiltersRequestBodyFromFilterParams(
      selectedTeam,
      searchParams,
      userInfo,
      filters
    );

    appendCustomRangeQueryParamsToRequest(params);
    if (!appendObjectives) yield put(showObjectiveLoader(true));

    if (via_view_dashboard && params?.status === 'NOT_STARTED')
      params.exclude_not_started_with_future_start_date = true;

    /* pagination start */
    const paginationData = yield select(getPaginationData);
    params.limit = disablePagination ? null : DefaultObjectivesCount;
    params.page = 1;
    if (!appendObjectives && paginationData.currentPage > 1)
      yield put(resetPaginationData());
    if (appendObjectives) params.page = paginationData.currentPage;
    /* pagination end */
    const { data } = yield objectivesApi.getFilteredObjectives(params);

    yield put(
      saveObjectives(
        data.objective_models,
        data.ordering_version,
        {
          total_objectives: data.total_objectives,
          metadata: data.metadata
        },
        appendObjectives && paginationData.currentPage > 1
      )
    );
    if (appendObjectives) {
      const lastObjectiveInViewId = yield select(getLastObjectiveInViewId);
      const ele = document.querySelector(
        `[data-id="${lastObjectiveInViewId}"]`
      );
      yield put(delay(100));
      if (lastObjectiveInViewId && ele)
        ele.scrollIntoView({ behavior: 'smooth' });
      yield put(setLastObjectiveInViewId(null));
    }
    if (callback) callback();
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
  } catch (e) {
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_filtered_objectives_worker', e);
  }
}

const getFilteredParams = (filters, selectedTeam, userInfo) => {
  const params = {
    user_id:
      selectedTeam.tag_id === HeaderLeftTabs[0].tag_id
        ? userInfo.user_reference_id
        : null,
    team_id:
      selectedTeam && !isHeaderLeftTabs(selectedTeam.tag_id)
        ? selectedTeam.tag_id
        : null,
    status: filters.status?.length ? filters.status.join() : null,
    objective_type:
      filters.objective_type ||
      (filters.objectives?.length ? filters.objectives.join() : null),
    okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
    // due_date_by: filters.due_date,
    objective_status: filters.negotiation_states
      ? filters.negotiation_states.join()
      : null,
    progress_higher_than: filters.progress_higher_than,
    progress_lower_than: filters.progress_lower_than,
    objectives_not_updated_from_days: filters.objectives_not_updated_from_days,
    level:
      selectedTeam?.tag_id === HeaderLeftTabs[2].tag_id ? filters.level : null
  };
  if (filters?.demographic_key) {
    params.demographic_key = filters?.demographic_key;
  }
  if (filters?.demographic_value) {
    params.demographic_value = filters?.demographic_value;
  }
  if (selectedTeam?.tag_id === HeaderLeftTabs[1].tag_id)
    params.user_hierarchy_type = 'DIRECT_REPORTS';
  appendCustomRangeQueryParamsToRequest(params);
  if (filters.manager_approval_status)
    params.manager_approval_status = filters.manager_approval_status;
  return params;
};

function* fetchDashboardFilteredObjectivesWorker({
  filters,
  appendObjectives
}) {
  try {
    const selectedTeam = yield select(getSelectedTeamSelector);
    const userInfo = yield select(getCurrentUserInfoSelector);
    const params = getFilteredParams(filters, selectedTeam, userInfo);
    /* pagination start */
    const paginationData = yield select(getPaginationData);
    params.limit = DefaultObjectivesCount;
    params.page = 1;
    if (!appendObjectives && paginationData.currentPage > 1)
      yield put(resetPaginationData());
    if (appendObjectives) params.page = paginationData.currentPage;
    /* pagination end */
    if (!appendObjectives) yield put(showObjectiveLoader(true));
    const { data } = yield objectivesApi.getDashboardFilterObjectives(params);
    yield put(
      saveObjectives(
        data.objective_models,
        data.ordering_version,
        {
          total_objectives: data.total_objectives,
          metadata: data.metadata
        },
        appendObjectives && paginationData.currentPage > 1
      )
    );
    if (appendObjectives) {
      const lastObjectiveInViewId = yield select(getLastObjectiveInViewId);
      const ele = document.querySelector(
        `[data-id="${lastObjectiveInViewId}"]`
      );
      yield put(delay(100));
      if (lastObjectiveInViewId && ele)
        ele.scrollIntoView({ behavior: 'smooth' });
      yield put(setLastObjectiveInViewId(null));
    }
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
  } catch (e) {
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_filtered_objectives_worker', e);
  }
}

function* fetchDashboardFilteredObjectivesForQuickFiltersWorker({
  filters,
  quickFiltersSelected,
  appendObjectives
}) {
  try {
    if (!appendObjectives) yield put(showObjectiveLoader(true));
    const filterCopy = {
      status: filters.status?.length ? filters.status.join() : null,
      objective_status: filters.negotiation_states
        ? filters.negotiation_states.join()
        : null,
      progress_higher_than: filters.progress_higher_than,
      progress_lower_than: filters.progress_lower_than,
      objectives_not_updated_from_days:
        filters.objectives_not_updated_from_days,
      ...quickFiltersSelected,
      objective_type:
        quickFiltersSelected.objective_type === ObjectiveTypes.company &&
        filters.objectives?.length &&
        filters.objectives[0] === ObjectiveTypes.company
          ? ObjectiveTypes.company
          : quickFiltersSelected.objective_type
    };
    if (filters?.demographic_key) {
      filterCopy.demographic_key = filters?.demographic_key;
    }
    if (filters?.demographic_value) {
      filterCopy.demographic_value = filters?.demographic_value;
    }
    /* pagination start */
    const paginationData = yield select(getPaginationData);
    filterCopy.limit = DefaultObjectivesCount;
    filterCopy.page = 1;
    if (!appendObjectives && paginationData.currentPage > 1)
      yield put(resetPaginationData());
    if (appendObjectives) filterCopy.page = paginationData.currentPage;
    /* pagination end */
    const {
      data
    } = yield objectivesApi.getDashboardFilterObjectivesForQuickFilters(
      filterCopy
    );
    yield put(
      saveObjectives(
        data.objective_models,
        data.ordering_version,
        {
          total_objectives: data.total_objectives,
          metadata: data.metadata
        },
        appendObjectives && paginationData.currentPage > 1
      )
    );
    if (appendObjectives) {
      const lastObjectiveInViewId = yield select(getLastObjectiveInViewId);
      const ele = document.querySelector(
        `[data-id="${lastObjectiveInViewId}"]`
      );
      yield put(delay(100));
      if (lastObjectiveInViewId && ele)
        ele.scrollIntoView({ behavior: 'smooth' });
      yield put(setLastObjectiveInViewId(null));
    }
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
  } catch (e) {
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_filtered_objectives_worker', e);
  }
}

function* fetchSearchResultsWorker({ search_term }) {
  try {
    yield put(showAppLoading());
    const requestParams = {
      okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE)
    };
    appendCustomRangeQueryParamsToRequest(requestParams);
    const { data } = yield objectivesApi.getSearchObjectiveResults(
      encodeURIComponent(search_term),
      requestParams
    );
    yield put(updateSearchQueryString(search_term));
    yield put(
      saveObjectives(data.objective_models, data.ordering_version, {
        total_objectives: data.total_objectives,
        metadata: data.metadata
      })
    );
    yield put(setFilters(null));
    analytics.trackEvent(
      'search_results',
      'search_results_count',
      `total_objectives:${data.objective_models.length}`,
      search_term.length
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_search_objectives_worker', e);
  }
}

function* fetchTeamDetailsWorker({ team_id }) {
  try {
    yield put(showAppLoading());
    const { data } = yield teamsApi.getTeamDetails(team_id);
    const teamTagDetails = { ...data.tags };
    delete data.tags;
    yield put(saveTeamDetails({ ...data, ...teamTagDetails }));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_team_details_worker', e);
  }
}

function* updateTeamNameWorker({ id, value }) {
  try {
    yield put(showAppLoading());
    const { data } = yield teamsApi.updateTeamName(id, value);
    yield put(
      fetchTeamsData('ORG', getValueFromQueryParam(QueryParam.SELECTED_TEAM))
    );
    yield put(fetchTeamsData('USER'));
    yield put(
      showAlertMessage('success', `Team name changed to ${data.label}`)
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_team_name_worker', e);
  }
}

function* updateTeamDescriptionWorker({ id, description }) {
  try {
    yield put(showAppLoading());
    yield teamsApi.updateTeamDescription(id, description);
    yield put(
      fetchTeamsData('ORG', getValueFromQueryParam(QueryParam.SELECTED_TEAM))
    );
    yield put(fetchTeamsData('USER'));
    yield put(showAlertMessage('success', 'Team description updated.'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_team_description_worker', e);
  }
}

function* deleteTeamWorker({ id }) {
  try {
    yield put(showAppLoading());
    const { data } = yield teamsApi.deleteTeam(id);
    yield put(
      fetchTeamsData('ORG', getValueFromQueryParam(QueryParam.SELECTED_TEAM))
    );
    yield put(fetchTeamsData('USER'));
    yield put(showAlertMessage('success', `Team ${data.label} deleted`));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('delete_team_worker', e);
  }
}

function* pasteObjectiveRequestWorker({ objective }) {
  try {
    yield put(showAppLoading());
    let selectedObjectiveToMove = yield select(
      getSelectedObjectiveToMoveSelector
    );
    // get updated objective that is to be moved
    const updatedObjectiveResponse = yield objectivesApi.getObjectiveDetail(
      selectedObjectiveToMove.objective_id,
      { INCLUDE_PARENT: true }
    );
    selectedObjectiveToMove = updatedObjectiveResponse.data;
    const pathMap = yield select(getPathMapSelector);

    if (
      pathMap[objective.objective_id][objective.rootObjectiveIndex].includes(
        selectedObjectiveToMove.objective_id
      )
    ) {
      yield put(showAlertMessage('error', ErrorMsg.OBJECTIVE_MOVE_TO_KRS));
      yield put(hideAppLoading());
      return;
    }

    const requestBody = {
      current_parent_objective_id: selectedObjectiveToMove.parent_objective_id,
      new_parent_objective_id: objective.objective_id
    };

    if (
      requestBody.current_parent_objective_id ===
      requestBody.new_parent_objective_id
    ) {
      yield put(
        showAlertMessage('error', ErrorMsg.OBJECTIVE_MOVE_TO_SAME_PARENT)
      );
      yield put(hideAppLoading());
      return;
    }

    const { data } = yield objectivesApi.alignObjective(
      selectedObjectiveToMove.objective_id,
      requestBody
    );

    yield put(
      storeRecentlyUpdatedObjectiveDetails(selectedObjectiveToMove.objective_id)
    );
    yield put(setMoveObjective(null));
    yield put(hideAppLoading());
    yield put(showAlertMessage('success', 'Objective moved successfully.'));
    yield put(
      deleteObjectiveInList({
        ...data,
        alignment_of: selectedObjectiveToMove.alignment_of
      })
    );
    yield delay(100);
    yield put(
      saveNewKR(
        { ...selectedObjectiveToMove, root_objective: false },
        objective.objective_id
      )
    );
    if (!objective.isOpen) {
      yield delay(100);
      yield put(fetchKeyResultsRequest(objective));
    }
    yield put(saveHighlightedObjective(selectedObjectiveToMove));
    yield put(showOKRHierarchyToast());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('paste_objective_request_worker', e);
  }
}

function* getUpdatedSelectedObjectiveWorker() {
  try {
    const selectedObjectiveToMove = yield select(
      getSelectedObjectiveToMoveSelector
    );
    const {
      data
    } = yield objectivesApi.getObjectiveDetail(
      selectedObjectiveToMove.objective_id,
      { INCLUDE_PARENT: true }
    );
    yield put(setMoveObjective(data));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get_updated_selected_objective_worker', e);
  }
}

function* postLabelObjective({ requestBody, objective }) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.postLabelObjective(requestBody);
    yield put(
      updateObjective({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(hideAppLoading());
    const type = objective.depth ? 'Key result' : 'Objective';
    if (requestBody.label_ids[0] === '1bf296d3-153e-4cd9-96fc-e26b29d29565')
      yield put(showAlertMessage('success', `${type} starred successfully`));
    if (requestBody.label_ids?.length === 0)
      yield put(showAlertMessage('success', `${type} un-starred successfully`));
  } catch (error) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
    analytics.trackError('post_label_objective', error);
  }
}

function* linkObjectiveRequestWorker({
  objective,
  objectiveToBeLinked,
  objective_prioritization_request,
  newPositionParams
}) {
  try {
    yield put(showAppLoading());
    const selectedObjectiveToLink = yield select(
      getSelectedObjectiveToLinkSelector
    );

    const objectiveToBeLink = selectedObjectiveToLink || objectiveToBeLinked;

    const updatedSelectedObjectiveToLink = yield objectivesApi
      .getObjectiveDetail(objectiveToBeLink.objective_id, {
        INCLUDE_PARENT: true
      })
      .then(res => res.data);
    // get updated objective that is to be linked
    const dependent_objective = yield objectivesApi
      .getObjectiveDetail(objective.objective_id, { INCLUDE_PARENT: true })
      .then(res => res.data);
    // check if the objective already has a dependency objective same as the linked one
    if (
      dependent_objective.key_results.length &&
      dependent_objective.key_results.filter(
        dependency_objective =>
          dependency_objective.objective_id ===
            updatedSelectedObjectiveToLink.objective_id &&
          dependency_objective.dependency_objective
      ).length > 0
    ) {
      yield put(showAlertMessage('error', ErrorMsg.SAME_OBJECTIVE_LINK_ERROR));
      yield put(hideAppLoading());
      return;
    }

    const pathMap = yield select(getPathMapSelector);

    // check if the objective is KR of the objective_to_be_linked.
    if (
      pathMap[objective.objective_id][objective.rootObjectiveIndex]?.includes(
        updatedSelectedObjectiveToLink.objective_id
      )
    ) {
      yield put(showAlertMessage('error', ErrorMsg.OBJECTIVE_IS_IN_KRS_ERROR));
      yield put(hideAppLoading());
      return;
    }

    // check if the objective is parent of the objective_to_be_linked
    if (
      dependent_objective.key_results.filter(
        key_result =>
          key_result.objective_id ===
          updatedSelectedObjectiveToLink.objective_id
      ).length > 0
    ) {
      yield put(
        showAlertMessage('error', ErrorMsg.OBJECTIVE_TO_LINK_IS_PARENT_ERROR)
      );
      yield put(hideAppLoading());
      return;
    }

    const requestBody = {
      dependent_objective_ids: [dependent_objective.objective_id],
      objective_prioritization_request
    };

    yield objectivesApi.createLinkedObjective(
      updatedSelectedObjectiveToLink.objective_id,
      requestBody
    );
    yield put(
      storeRecentlyUpdatedObjectiveDetails(
        updatedSelectedObjectiveToLink.objective_id
      )
    );
    yield put(setObjectiveToLink(null));
    yield put(hideAppLoading());
    const objectivesList = yield select(getObjectivesSelector);

    const currentStateOfObjectiveToLink = clone(
      getCurrentVersionOfObjective(
        objectivesList,
        objectiveToBeLink.objective_id,
        pathMap[objectiveToBeLink.objective_id]
      )
    );
    yield put(
      updateObjective(
        {
          ...currentStateOfObjectiveToLink,
          dependent_objectives_count:
            currentStateOfObjectiveToLink.dependent_objectives_count + 1
        },
        true
      )
    );

    if (newPositionParams) {
      yield put(
        reorderObjectivesUnderDiffParent(
          { ...objectiveToBeLinked },
          newPositionParams?.objective_id,
          newPositionParams?.parent_objective_id,
          newPositionParams?.destinationIndex,
          newPositionParams?.sourceIndex,
          newPositionParams?.positionIndex
        )
      );
    } else {
      yield put(
        saveNewDependencyObjective(
          {
            ...updatedSelectedObjectiveToLink,
            root_objective: false,
            dependency_objective: true
          },
          objective.objective_id
        )
      );
    }
    if (!objective.isOpen) {
      yield delay(100);
      yield put(fetchKeyResultsRequest(objective));
    }
    yield put(
      saveHighlightedObjective({
        ...updatedSelectedObjectiveToLink,
        dependency_objective: true,
        parent_objective_id: objective.objective_id
      })
    );
    if (objectiveToBeLink.dependency_objective || objectiveToBeLinked) {
      // in case of moving linked objective to new parent
      yield put(
        unLinkObjectiveRequest(
          objectiveToBeLink.objective_id,
          objectiveToBeLink.parent_objective_id
        )
      );
    }
    yield put(showAlertMessage('success', 'Objective linked successfully.'));
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('link_objective_worker', e);
  }
}

function* unLinkObjectiveRequestWorker({
  linked_objective_id,
  dependent_objective_id
}) {
  try {
    yield put(showAppLoading());
    // remove from path map and update objectives
    yield objectivesApi.deleteLinkedObjective(linked_objective_id, {
      dependent_objective_ids: [dependent_objective_id]
    });
    yield put(showAlertMessage('success', 'Objective unlinked successfully'));
    yield put(
      deleteDependencyFromObjectives(
        [dependent_objective_id],
        linked_objective_id,
        false
      )
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('unlink_objective_worker', e);
  }
}

function* fetchLinkedObjectivesWorker({ objective }) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.getLinkedToObjectives(
      objective.objective_id,
      isStrategicCycle(objective.objective_cycle.cycle_type)
    );
    const linkedObjectives = data.objective_models.map(linked_objective => ({
      ...linked_objective,
      dependent_on: objective.objective_id
    }));
    yield put(saveLinkedObjectives(linkedObjectives));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_linked_objective_worker', e);
  }
}

function* fetchViedDashboardData({
  dashaboardParams,
  filterParams,
  disableLoading
}) {
  try {
    if (!disableLoading) yield put(showAppLoading());
    const allDashboardActionTypes = Object.keys(ViewDashboardConfig).map(
      key => ViewDashboardConfig[key].actionType
    );
    let data = [];
    // If the tabs selected are "my objectives" or "my directs", then we need to fetch the data for the selected tabs
    // call all the sub apis for the above 2 tabs
    if (dashaboardParams.user_hierarchy_type || dashaboardParams.user_id) {
      const parallelData = yield all(
        allDashboardActionTypes.map(type => {
          if (type === 'objective-last-updated-details') {
            const request = { ...dashaboardParams };
            request.objectives_not_updated_from_days =
              storage.getDataFromLocal(StorageKey.ORG_SETTINGS)
                ?.objectives_not_updated_from_days || 15;
            return objectivesApi
              .getDashboardData(type, request)
              .then(res => ({ type, data: res.data }));
          }
          return objectivesApi
            .getDashboardData(type, dashaboardParams)
            .then(res => ({ type, data: res.data }));
        })
      );
      data = {
        objective_summary: parallelData,
        demographic_view: null
      };
    } else {
      // this is the case where demographic based summary data is required
      let objectiveDemographicSummary = null;
      if (filterParams && Object.keys(filterParams).length > 0) {
        //  if filters is aplied then call the api with filters
        const selectedTeam = yield select(getSelectedTeamSelector);
        const userInfo = yield select(getCurrentUserInfoSelector);
        const searchParams = yield select(getSearchObjectiveParams);

        const filterParamsRequestBody = getFiltersRequestBodyFromFilterParams(
          selectedTeam,
          searchParams,
          userInfo,
          filterParams
        );
        const requestBody = {
          ...filterParamsRequestBody,
          ...dashaboardParams
        };
        objectiveDemographicSummary = yield objectivesApi.getFilteredObjectivesWithDemographics(
          requestBody
        );
      } else {
        objectiveDemographicSummary = yield objectivesApi.getObjectivesSummary(
          dashaboardParams
        );
      }

      data = transformDemographicObjectiveSummary(
        objectiveDemographicSummary.data
      );
    }
    yield put(saveViewDashboardData(data));
    if (!disableLoading) yield put(hideAppLoading());
  } catch (e) {
    yield put(saveViewDashboardData(null));
    if (!disableLoading) yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_view_dashboard_data_worker', e);
  }
}

function* fetchViewDashboardDataWithQuickFilters({
  requestBody,
  selectedDemographicKey
}) {
  try {
    const objectiveDemographicSummary = yield objectivesApi.getDashboardDataWithQuickFiltersV2(
      { ...requestBody, demographic_key: selectedDemographicKey }
    );

    yield put(
      saveViewDashboardData(
        transformDemographicObjectiveSummary(objectiveDemographicSummary.data)
      )
    );
  } catch (e) {
    yield put(saveViewDashboardData(null));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError(
      'fetch_view_dashboard_data_with_quick_filters_worker',
      e
    );
  }
}

function* triggerObjectiveRealtimeUpdate({
  objective_id,
  team_id,
  user_reference_id,
  isSearchActive,
  is_present_in_view
}) {
  try {
    const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
    const isHeaderLeftTab = isHeaderLeftTabs(team_id);
    const isMyObjective = team_id === HeaderLeftTabs[0].tag_id;
    const objectiveBelongsToADifferentUser =
      data?.owner_detail?.user_reference_id !== user_reference_id;
    const isCurrentTeamRemoved = !data?.tags?.filter(
      tag => tag.tag_id === team_id
    ).length;
    const in_view_obj_owner_updated =
      isMyObjective && objectiveBelongsToADifferentUser && is_present_in_view;
    const out_view_obj_owner_updated_to_current_user =
      isMyObjective && !objectiveBelongsToADifferentUser && !is_present_in_view;
    const in_view_obj_team_updated =
      !isHeaderLeftTab && isCurrentTeamRemoved && is_present_in_view;
    const out_view_obj_team_updated_to_current_team =
      !isHeaderLeftTab && !isCurrentTeamRemoved && !is_present_in_view;
    let reasonForToastDisplay = '';
    if (in_view_obj_owner_updated)
      reasonForToastDisplay =
        'Objective owner updated (an objective in the view has been unassigned).';
    if (out_view_obj_owner_updated_to_current_user)
      reasonForToastDisplay =
        'Objective owner updated (new objective assigned).';
    if (in_view_obj_team_updated)
      reasonForToastDisplay = 'Objective team updated (Current team: removed).';
    if (out_view_obj_team_updated_to_current_team)
      reasonForToastDisplay = 'Objective team updated (Current team: added).';
    if (
      in_view_obj_owner_updated ||
      out_view_obj_owner_updated_to_current_user ||
      in_view_obj_team_updated ||
      out_view_obj_team_updated_to_current_team
    ) {
      const alertAction = {
        actionImage: '/assets/images/refresh-white.svg',
        actionImageAltText: 'Refresh',
        actionType: ToastActionType.REFRESH_OBJECTIVES
      };
      if (!isSearchActive) yield put(updateObjectivesDataRealtimeEvent(data));
      else
        yield put(
          updateObjectivesDataRealtimeEvent(data, false, true, isSearchActive)
        );
      if (!isSearchActive) {
        analytics.trackEvent(
          AnalyticsCategory.real_time_events,
          'View updated toast',
          reasonForToastDisplay
        );
        yield put(
          showAlertMessage(
            AlertActionTypes.VIEW_UPDATED,
            'View Updated',
            alertAction
          )
        );
      }
    } else {
      yield put(updateObjectivesDataRealtimeEvent(data, false, true));
    }
  } catch (e) {
    analytics.trackError('update_objective_realtime_event', e);
  }
}

function* fetchObjectivesForSelectedParamsWorker({
  shouldApplyFilter,
  appendObjectives,
  disablePagination,
  callback
}) {
  try {
    yield put(showAppLoading());
    const showCompanyObjective = yield select(showCompanyObjectiveSelector);
    const showIndependentObjective = yield select(
      showIndependentObjectiveSelector
    );
    const filters = yield select(getFilterParamsSelector);
    if (
      shouldApplyFilter &&
      filters &&
      !(Object.keys(filters).length === 1 && filters.exclude_if_due_date_passed)
    ) {
      yield put(
        fetchFilteredObjectives(
          filters,
          false,
          null,
          appendObjectives,
          disablePagination
        )
      );
    } else {
      const selectedTeam = yield select(getSelectedTeamSelector);
      const selectedCycle = yield select(getSelectedObjectiveCycleSelector);
      const params = {
        user_hierarchy_type:
          selectedTeam.tag_id === HeaderLeftTabs[1].tag_id
            ? 'DIRECT_REPORTS'
            : null,
        tag_id: !isHeaderLeftTabs(
          getValueFromQueryParam(QueryParam.SELECTED_TEAM)
        )
          ? getValueFromQueryParam(QueryParam.SELECTED_TEAM)
          : null,
        user_id:
          selectedTeam &&
          (HeaderLeftTabs[0].tag_id === selectedTeam.tag_id ||
            HeaderLeftTabs[0].tag_id ===
              getValueFromQueryParam(QueryParam.SELECTED_TEAM))
            ? storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID)
            : null,
        objective_type:
          selectedTeam &&
          `${showCompanyObjective ? ObjectiveTypes.company : ''}${
            showIndependentObjective
              ? `${showCompanyObjective ? ',' : ''}${ObjectiveTypes.nonAligned}`
              : ''
          }`
      };
      if (filters?.exclude_if_due_date_passed)
        params.exclude_if_due_date_passed = true;
      if (getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE))
        params.okr_cycle_id = getValueFromQueryParam(
          QueryParam.OBJECTIVE_CYCLE
        );
      else if (selectedCycle) {
        params.date_range_start = selectedCycle?.start_date;
        params.date_range_end = selectedCycle?.end_date;
      }
      appendCustomRangeQueryParamsToRequest(params);
      // changing to call, to ensure the saga waits for the API call to complete before continuing
      yield call(fetchObjectivesRequestWorker, {
        requestBody: params,
        callback: null,
        appendObjectives
      });
    }
    const viewAlignmentPreviousViewState = yield select(
      getViewAlignmentPreviousViewState
    );
    if (viewAlignmentPreviousViewState)
      yield put(viewInContextRequest(viewAlignmentPreviousViewState.objective));
    if (callback) callback();
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('disable_search_load_objectives_worker', e);
  }
}

function* disableSearchAndLoadObjectivesWorker() {
  try {
    yield put(showAppLoading());
    const filters = yield select(getSearchObjectiveParams);
    let preSearchFilters = clone(filters.preSearchFilters);
    if (Object.keys(preSearchFilters).length === 0) preSearchFilters = null;
    yield put(resetSearchParamsAction());
    if (preSearchFilters) {
      yield put(setFilters(preSearchFilters));
    }
    yield put(fetchObjectivesForSelectedParams(true));

    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('disable_search_load_objectives_worker', e);
  }
}

function* updateObjectiveVisibilityWorker({
  requestBody,
  objective,
  updateSelectedObjective
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateObjective(
      objective.objective_id,
      { privacy_details: requestBody, version: objective.version }
    );
    if (updateSelectedObjective) {
      yield put(setSelectedObjective(data, requestBody.parent_objective_owner));
      yield put(fetchObjectiveDetails(objective.objective_id));
    }
    yield put(
      updateObjectivesVisibility({
        ...data,
        rootObjectiveIndex: objective.rootObjectiveIndex
      })
    );
    yield put(showAlertMessage('success', 'Objective visibility updated.'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_objective_visibility', e);
  }
}

function* unLinkWithGoogleSheetsWorker({ objective_id, version }) {
  try {
    yield put(showAppLoading());
    yield sheetsIntegrationApi.deleteTracker(objective_id, version);
    const { data } = yield objectivesApi.getObjectiveDetail(objective_id);
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    yield put(setSelectedObjective(data));
    yield put(fetchObjectiveActivities(objective_id));
    yield put(updateObjective(data));
    yield put(showAlertMessage('success', 'Automation Removed successfully.'));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('unlink_with_sheets', e);
  }
}

function* fetchQuickFilterObjectivesWorker({ requestBody, appendObjectives }) {
  try {
    if (!appendObjectives) yield put(showObjectiveLoader(true));
    /* pagination start */
    const paginationData = yield select(getPaginationData);
    requestBody.limit = DefaultObjectivesCount;
    requestBody.page = 1;
    if (!appendObjectives && paginationData.currentPage > 1)
      yield put(resetPaginationData());
    if (appendObjectives) requestBody.page = paginationData.currentPage;
    /* pagination end */
    const { data } = yield objectivesApi.getQuickFilterObjectives(requestBody);
    yield put(
      saveObjectives(
        data.objective_models,
        data.ordering_version,
        {
          total_objectives: data.total_objectives,
          metadata: data.metadata
        },
        appendObjectives && paginationData.currentPage > 1
      )
    );
    if (appendObjectives) {
      const lastObjectiveInViewId = yield select(getLastObjectiveInViewId);
      const ele = document.querySelector(
        `[data-id="${lastObjectiveInViewId}"]`
      );
      yield put(delay(100));
      if (lastObjectiveInViewId && ele)
        ele.scrollIntoView({ behavior: 'smooth' });
      yield put(setLastObjectiveInViewId(null));
    }
    yield put(setQuickFiltersSelected(requestBody));
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
  } catch (e) {
    if (!appendObjectives) yield put(showObjectiveLoader(false));
    else yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get quick filter objectives', e);
  }
}

function* updateTeamQuickFilterAttributesWorker() {
  try {
    const selectedTeam = yield select(getSelectedTeamSelector);
    if (selectedTeam && !isHeaderLeftTabs(selectedTeam.tag_id)) {
      const selectedObjectiveCycle = yield select(
        getSelectedObjectiveCycleSelector
      );
      const request = {
        root_team_id: selectedTeam.tag_id,
        okr_cycle_id: selectedObjectiveCycle?.id
      };
      appendCustomRangeQueryParamsToRequest(request);
      const info = yield objectivesApi
        .getFilterAttributes(request)
        .then(res => res.data);
      yield put(setQuickFilterAttributes(info, true));
    }
  } catch (e) {
    analytics.trackError('update_team_quick_filter_attributes_worker', e);
  }
}

function* loadMoreWorker() {
  try {
    yield put(updatePageLoadInProgress(true));
    const selectedTeamQuickFilters = yield select(getSelectedTeamQuickFilters);
    const quickFiltersSelected = yield select(getSelectedQuickFilters);
    const dashboardFilters = yield select(getDashboardFilterSelector);
    const selectedTeam = yield select(getSelectedTeamSelector);
    const isTeamSpace = !isHeaderLeftTabs(selectedTeam.tag_id);
    if (isTeamSpace && selectedTeamQuickFilters) {
      yield put(fetchQuickFilterObjectives(selectedTeamQuickFilters, true));
      return;
    }
    if (dashboardFilters) {
      if (!quickFiltersSelected)
        yield put(fetchDashboardFilteredObjectives(dashboardFilters, true));
      else
        yield put(
          fetchDashboardFilteredObjectivesForQuickFiltersWorker(
            dashboardFilters,
            true,
            true
          )
        );
      return;
    }
    yield put(fetchObjectivesForSelectedParams(true, true));
  } catch (e) {
    yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('load_more_worker', e);
  }
}

function* getObjectivesForRemindersInDashboardViewWorker({
  filters,
  callback
}) {
  try {
    const selectedTeam = yield select(getSelectedTeamSelector);
    const userInfo = yield select(getCurrentUserInfoSelector);
    const params = getFilteredParams(filters, selectedTeam, userInfo);
    const { data } = yield objectivesApi.getDashboardFilterObjectives(params);
    callback(data.objective_models);
  } catch (e) {
    yield put(updatePageLoadInProgress(false));
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('load_more_worker', e);
  }
}

function* exportCurrentViewWorker() {
  try {
    yield put(showAppLoading());
    analytics.trackEvent(
      AnalyticsCategory.cta_click,
      'export_current_view_okr'
    );
    const filters = yield select(getFilterParamsSelector);
    const showCompanyObjective = yield select(showCompanyObjectiveSelector);
    const showIndependentObjective = yield select(
      showIndependentObjectiveSelector
    );
    const searchParams = yield select(getSearchObjectiveParams);
    const userInfo = yield select(getCurrentUserInfoSelector);
    const selectedTeamQuickFilters = yield select(getSelectedTeamQuickFilters);
    const selectedQuickFilters = yield select(getSelectedQuickFilters);
    const dashboardFilters = yield select(getDashboardFilterSelector);
    const selectedTeam = yield select(getSelectedTeamSelector);
    const isTeamSpace = !isHeaderLeftTabs(selectedTeam.tag_id);
    let params = null;
    let data = null;
    if (!dashboardFilters && isTeamSpace && selectedTeamQuickFilters) {
      ({ data } = yield objectivesApi.getQuickFilterObjectives(
        selectedTeamQuickFilters
      ));
      if (window.pendo)
        window.pendo.track(AnalyticsCategory.export_current_view, {
          team_quick_filter_view: true
        });
    } else if (dashboardFilters) {
      if (!selectedQuickFilters) {
        params = {
          user_id:
            selectedTeam.tag_id === HeaderLeftTabs[0].tag_id
              ? userInfo.user_reference_id
              : null,
          team_id:
            selectedTeam && !isHeaderLeftTabs(selectedTeam.tag_id)
              ? selectedTeam.tag_id
              : null,
          status: dashboardFilters.status?.length
            ? dashboardFilters.status.join()
            : null,
          objective_type:
            dashboardFilters.objective_type ||
            (dashboardFilters.objectives?.length
              ? dashboardFilters.objectives.join()
              : null),
          okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
          // due_date_by: filters.due_date,
          objective_status: dashboardFilters.negotiation_states
            ? dashboardFilters.negotiation_states.join()
            : null,
          progress_higher_than: dashboardFilters.progress_higher_than,
          progress_lower_than: dashboardFilters.progress_lower_than,
          objectives_not_updated_from_days:
            dashboardFilters.objectives_not_updated_from_days,
          level:
            selectedTeam?.tag_id === HeaderLeftTabs[2].tag_id
              ? dashboardFilters.level
              : null
        };
        if (selectedTeam?.tag_id === HeaderLeftTabs[1].tag_id)
          params.user_hierarchy_type = 'DIRECT_REPORTS';
        appendCustomRangeQueryParamsToRequest(params);
        if (dashboardFilters.manager_approval_status)
          params.manager_approval_status =
            dashboardFilters.manager_approval_status;
        ({ data } = yield objectivesApi.getDashboardFilterObjectives(params));
        if (window.pendo)
          window.pendo.track(AnalyticsCategory.export_current_view, {
            isTeamSpace,
            dashboard_filter_view: true
          });
      } else {
        params = {
          status: dashboardFilters.status?.length
            ? dashboardFilters.status.join()
            : null,
          objective_status: dashboardFilters.negotiation_states
            ? dashboardFilters.negotiation_states.join()
            : null,
          progress_higher_than: dashboardFilters.progress_higher_than,
          progress_lower_than: dashboardFilters.progress_lower_than,
          objectives_not_updated_from_days:
            dashboardFilters.objectives_not_updated_from_days,
          ...selectedQuickFilters,
          objective_type:
            selectedQuickFilters.objective_type === ObjectiveTypes.company &&
            dashboardFilters.objectives?.length &&
            dashboardFilters.objectives[0] === ObjectiveTypes.company
              ? ObjectiveTypes.company
              : selectedQuickFilters.objective_type
        };
        ({
          data
        } = yield objectivesApi.getDashboardFilterObjectivesForQuickFilters(
          params
        ));
        if (window.pendo)
          window.pendo.track(AnalyticsCategory.export_current_view, {
            isTeamSpace,
            dashboard_quick_filter_view: true
          });
      }
    } else if (
      filters &&
      !(Object.keys(filters).length === 1 && filters.exclude_if_due_date_passed)
    ) {
      params = {
        user_id:
          selectedTeam.tag_id === HeaderLeftTabs[0].tag_id &&
          !(searchParams && searchParams.searchActive)
            ? userInfo.user_reference_id
            : filters.owners?.length
            ? filters.owners.join()
            : null,
        team_id:
          selectedTeam &&
          !isHeaderLeftTabs(selectedTeam.tag_id) &&
          !(searchParams && searchParams.searchActive)
            ? selectedTeam.tag_id
            : filters.teams?.length
            ? filters.teams.join()
            : null,
        tag_id: filters.tags?.length ? filters.tags.join() : null,
        status: filters.status?.length ? filters.status.join() : null,
        objective_type: filters.objective_type,
        okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
        label_ids: filters.label_ids?.length ? filters.label_ids.join() : null,
        user_hierarchy_type:
          selectedTeam.tag_id === HeaderLeftTabs[1].tag_id
            ? 'DIRECT_REPORTS'
            : null,
        due_date_by: filters.due_date,
        objective_status:
          (filters.negotiation_states
            ? filters.negotiation_states.join()
            : null) ||
          (filters.objective_status ? filters.objective_status.join() : null),
        team_id_relation: filters.isTeamIntersection ? 'INTERSECTION' : 'UNION',
        tag_id_relation: filters.isTagIntersection ? 'INTERSECTION' : 'UNION',
        progress_higher_than: filters.progress_higher_than,
        progress_lower_than: filters.progress_lower_than,
        objectives_not_updated_from_days:
          filters.objectives_not_updated_from_days,
        search_query:
          searchParams && searchParams.searchTerm
            ? searchParams.searchTerm
            : null,
        level: filters.level,
        exclude_if_due_date_passed: !!filters.exclude_if_due_date_passed
      };
      appendCustomRangeQueryParamsToRequest(params);
      if (params?.status === 'NOT_STARTED')
        params.exclude_not_started_with_future_start_date = true;
      if (filters.objective_type)
        params.objective_type = filters.objective_type;
      if (filters.level) params.level_filter_type = 'SPECIFIC_LEVEL';
      ({ data } = yield objectivesApi.getFilteredObjectives(params));
      if (window.pendo)
        window.pendo.track(AnalyticsCategory.export_current_view, {
          isTeamSpace,
          filtered_view: true
        });
    } else {
      params = {
        user_hierarchy_type:
          selectedTeam.tag_id === HeaderLeftTabs[1].tag_id
            ? 'DIRECT_REPORTS'
            : null,
        tag_id: !isHeaderLeftTabs(
          getValueFromQueryParam(QueryParam.SELECTED_TEAM)
        )
          ? getValueFromQueryParam(QueryParam.SELECTED_TEAM)
          : null,
        user_id:
          selectedTeam &&
          (HeaderLeftTabs[0].tag_id === selectedTeam.tag_id ||
            HeaderLeftTabs[0].tag_id ===
              getValueFromQueryParam(QueryParam.SELECTED_TEAM))
            ? storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID)
            : null,
        objective_type:
          selectedTeam && selectedTeam.tag_id === HeaderLeftTabs[2].tag_id
            ? `${showCompanyObjective ? ObjectiveTypes.company : ''}${
                showIndependentObjective
                  ? `${showCompanyObjective ? ',' : ''}${
                      ObjectiveTypes.nonAligned
                    }`
                  : ''
              }`
            : null
      };
      if (filters?.exclude_if_due_date_passed)
        params.exclude_if_due_date_passed = true;
      if (getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE))
        params.okr_cycle_id = getValueFromQueryParam(
          QueryParam.OBJECTIVE_CYCLE
        );
      appendCustomRangeQueryParamsToRequest(params);
      ({ data } = yield objectivesApi.getObjectivesList(params));
      if (window.pendo)
        window.pendo.track(AnalyticsCategory.export_current_view, {
          isTeamSpace,
          default_view: true
        });
    }
    const showProtectedObjectives = yield select(
      getShowProtectedObjectivesSelector
    );
    const showCustomObjectives = yield select(getShowCustomObjectivesSelector);
    const date = new Date();
    const team = selectedTeam && selectedTeam.label;
    let isFiltered = false;
    if (filters || dashboardFilters) {
      isFiltered = true;
    }
    const filename = `${isFiltered ? 'filtered_' : ''}${team}_${
      date && date.toString().substr(0, 15)
    }.csv`;
    const objectivesList = yield select(getObjectivesSelector);
    data.objective_models = data.objective_models.map(objective => {
      const objIndex = objectivesList?.findIndex(
        ({ objective_id }) => objective_id === objective.objective_id
      );
      if (objIndex > -1) return objectivesList[objIndex];
      return objective;
    });
    exportObjectivesUtil.exportObjective(data.objective_models, filename, {
      showProtectedObjectives,
      showCustomObjectives
    });
    yield put(hideAppLoading());
  } catch (err) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', err.message));
    analytics.trackError('export_current_view_worker', err);
  }
}

function* updateAutoRollUpForObjective({
  objective,
  requestBody,
  isObjectiveDetailsOpen
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield objectivesApi.updateAutoRollUpAttributes(
      objective.objective_id,
      requestBody
    );
    if (isObjectiveDetailsOpen) {
      yield put(storeRecentlyUpdatedObjectiveDetails(objective.objective_id));
      yield put(setSelectedObjective(data));
      yield put(fetchObjectiveActivities(objective.objective_id));
    }

    yield put(updateObjective(data));
    yield put(hideAppLoading());
  } catch (err) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', err.message));
    analytics.trackError('updateAutoRollUpForObjective', err);
  }
}

export default function* defaultSaga() {
  yield takeLatest(
    actions.FETCH_OBJECTIVES_REQUEST,
    fetchObjectivesRequestWorker
  );
  yield takeLatest(actions.UPDATE_OWNER_REQUEST, updateOwnerRequestWorker);
  yield takeLatest(
    actions.UPDATE_PROGRESS_REQUEST,
    updateProgressRequestWorker
  );
  yield takeLatest(actions.UPDATE_TEAMS_REQUEST, updateTeamsRequestWorker);
  yield takeLatest(actions.UPDATE_STATUS_REQUEST, updateStatusRequestWorker);
  yield takeLatest(
    actions.UPDATE_MANAGER_APPROVAL_STATUS_REQUEST,
    updateManagerApprovalStatusRequestWorker
  );
  yield takeLatest(
    actions.RESEND_FOR_MANAGER_APPROVAL_REQUEST,
    resendForManagerApprovalRequestWorker
  );
  yield takeLatest(actions.UPDATE_TEAM_FOLLOWER, updateTeamFollowerWorker);
  yield takeLatest(actions.UPDATE_TEAM_OWNER, updateTeamOwnerWorker);
  yield takeLatest(actions.DELETE_OBJECTIVE, deleteObjectiveWorker);
  yield takeLatest(
    actions.FETCH_OBJECTIVE_KEY_RESULTS,
    fetchKeyResultsRequestWorker
  );
  yield takeLatest(
    actions.TOGGLE_OBJECTIVES_KR_MOBILE,
    toggleObjectiveKrsMobileWorker
  );
  yield takeLatest(
    actions.GO_BACK_TO_PREVIOUS_STATE_MOBILE,
    goBackToPreviousStateMobileWorker
  );
  yield takeLatest(actions.RESET_TREE_TRAVERSE_STATE, exitTreeViewWorker);
  yield takeLatest(
    actions.FETCH_SELECTED_OBJECTIVE,
    fetchSelectedObjectiveWorker
  );
  yield takeLatest(
    actions.FETCH_OBJECTIVE_ALIGNMENT,
    fetchObjectiveAlignmentWorker
  );
  yield takeLatest(
    actions.FETCH_FILTERED_OBJECTIVES,
    fetchFilteredObjectivesWorker
  );
  yield takeLatest(actions.FETCH_TEAM_DETAILS, fetchTeamDetailsWorker);
  yield takeLatest(actions.UPDATE_TEAM_NAME, updateTeamNameWorker);
  yield takeLatest(
    actions.UPDATE_TEAM_DESCRIPTION,
    updateTeamDescriptionWorker
  );
  yield takeLatest(actions.DELETE_TEAM, deleteTeamWorker);
  yield takeLatest(
    actions.PASTE_OBJECTIVE_REQUEST,
    pasteObjectiveRequestWorker
  );
  yield takeLatest(
    actions.GET_UPDATED_SELECTED_OBJECTIVE,
    getUpdatedSelectedObjectiveWorker
  );
  yield takeLatest(actions.POST_LABEL_OBJECTIVE, postLabelObjective);
  yield takeLatest(actions.UPDATE_DUE_DATE_REQUEST, updateDueDateRequestWorker);
  yield takeLatest(
    actions.UPDATE_START_DATE_REQUEST,
    updateStartDateRequestWorker
  );
  yield takeLatest(actions.LINK_TO_OBJECTIVE, linkObjectiveRequestWorker);
  yield takeLatest(actions.UNLINK_OBJECTIVE, unLinkObjectiveRequestWorker);
  yield takeLatest(
    actions.FETCH_LINKED_OBJECTIVES,
    fetchLinkedObjectivesWorker
  );
  yield takeLatest(actions.FETCH_SEARCH_RESULTS, fetchSearchResultsWorker);
  yield takeEvery(actions.FETCH_VIEW_DASHBOARD_DATA, fetchViedDashboardData);
  yield takeLatest(
    actions.FETCH_VIEW_DASHBOARD_DATA_WITH_QUICK_FILTERS,
    fetchViewDashboardDataWithQuickFilters
  );
  yield takeLatest(
    actions.FETCH_DASHBOARD_FILTERED_OBJECTIVES,
    fetchDashboardFilteredObjectivesWorker
  );
  yield takeLatest(
    actions.FETCH_DASHBOARD_FILTERED_OBJECTIVES_FOR_QUICK_FILTERS,
    fetchDashboardFilteredObjectivesForQuickFiltersWorker
  );
  yield takeLatest(
    actions.TRIGGER_OBJECTIVE_REALTIME_EVENT,
    triggerObjectiveRealtimeUpdate
  );
  yield takeLatest(
    appActions.DISABLE_SEARCH_ACTION,
    disableSearchAndLoadObjectivesWorker
  );
  yield takeLatest(
    appActions.FETCH_OBJECTIVES_FOR_SELECTED_PARAMS,
    fetchObjectivesForSelectedParamsWorker
  );
  yield takeLatest(
    actions.UPDATE_OBJECTIVE_VISIBILITY,
    updateObjectiveVisibilityWorker
  );
  yield takeLatest(
    actions.UNLINK_WITH_GOOGLE_SHEETS,
    unLinkWithGoogleSheetsWorker
  );
  yield takeLatest(
    actions.FETCH_QUICK_FILTER_OBJECTIVES,
    fetchQuickFilterObjectivesWorker
  );
  yield takeLatest(
    actions.UPDATE_TEAM_QUICK_FILTER_ATTRIBUTES,
    updateTeamQuickFilterAttributesWorker
  );
  yield takeLatest(actions.GOTO_NEXT_PAGE, loadMoreWorker);
  yield takeLatest(
    actions.EXPORT_CURRENT_VIEW_OBJECTIVES,
    exportCurrentViewWorker
  );
  yield takeLatest(
    actions.ALL_OBJECTIVES_FOR_REMINDER_DASHBOARD_VIEW,
    getObjectivesForRemindersInDashboardViewWorker
  );
  yield takeLatest(
    actions.UPDATE_MANAGER_APPROVAL_STATUS_FOR_KRS,
    updateManagerApprovalStatusForKrsRequestWorker
  );
  yield takeLatest(
    actions.UPDATE_AUTOROLLUP_ATTRIBUTES_FOR_OBJECTIVE,
    updateAutoRollUpForObjective
  );
}
